import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { API_BASE } from './config';
import '../css/RankTable.css';


const RANKS = [
  'ALL',
  'BG',
  'BGM',
  'NB',
  'NB+',
  'N-',
  'N',
  'N/S',
  'S',
  'S/P'
];
const getAvatarSource = avatarUrl => {
  if (!avatarUrl) return '';

  const value = String(avatarUrl).trim();

  // รูปจาก LINE หรือ URL ภายนอก
  if (
    value.startsWith('http://') ||
    value.startsWith('https://') ||
    value.startsWith('data:') ||
    value.startsWith('blob:')
  ) {
    return value;
  }

  // รูปจาก Backend /uploads
  return `${API_BASE}${value.startsWith('/') ? '' : '/'}${value}`;
};

function RankTable() {

  /* =================================================
     NAVIGATE
  ================================================= */

  const navigate = useNavigate();

  const openMonitor = () => {
    navigate('/monitor/1');
  };
const openPlayerHistory = player => {
  if (!player?.user_id) return;

  navigate(
    `/player-history/1/${player.user_id}`
  );
};

  /* =================================================
     STATE
  ================================================= */

  const [players, setPlayers] = useState([]);

  const [rankFilter, setRankFilter] =
    useState('ALL');

  const [search, setSearch] =
    useState('');

  const [loading, setLoading] =
    useState(false);


  /* =================================================
     LOAD
  ================================================= */

  const loadRankTable = async () => {

    setLoading(true);

    try {

      const params =
        new URLSearchParams();

      if (rankFilter !== 'ALL') {
        params.set(
          'rank',
          rankFilter
        );
      }

      const query =
        params.toString();

      const url =
        `${API_BASE}/api/game-match/rank-table` +
        `${query ? `?${query}` : ''}`;

      const res =
        await fetch(url);

      const data =
        await res.json();

      if (!res.ok) {
        throw new Error(
          data.message ||
          'Load rank failed'
        );
      }

      setPlayers(
        Array.isArray(data)
          ? data
          : []
      );

    } catch (err) {

      console.error(
        '[LOAD RANK ERROR]',
        err
      );

      setPlayers([]);

    } finally {

      setLoading(false);

    }
  };


  useEffect(() => {
    loadRankTable();
  }, [rankFilter]);


  /* =================================================
     SEARCH
  ================================================= */

  const filteredPlayers =
    useMemo(() => {

      const keyword =
        search
          .trim()
          .toLowerCase();

      if (!keyword) {
        return players;
      }

      return players.filter(
        player =>
          String(
            player.name || ''
          )
            .toLowerCase()
            .includes(keyword)
      );

    }, [players, search]);


  /* =================================================
     MOVEMENT
  ================================================= */

  const renderMovement = player => {

    if (
      player.movement === 'UP'
    ) {
      return (
        <span className="movement-up">
          ▲ {player.movement_count}
        </span>
      );
    }

    if (
      player.movement === 'DOWN'
    ) {
      return (
        <span className="movement-down">
          ▼ {player.movement_count}
        </span>
      );
    }

    return (
      <span className="movement-same">
        ━
      </span>
    );
  };


  /* =================================================
     POINT CHANGE
  ================================================= */

  const renderPointChange = value => {

    const point =
      Number(value || 0);

    if (point > 0) {
      return (
        <span className="change-plus">
          +{point}
        </span>
      );
    }

    if (point < 0) {
      return (
        <span className="change-minus">
          {point}
        </span>
      );
    }

    return (
      <span className="change-zero">
        0
      </span>
    );
  };


  /* =================================================
     RENDER
  ================================================= */

  return (
    <div className="rank-page">

      {/* ================= HEADER ================= */}

      <div className="rank-header">

        <div>

          <h2>
            🏆 Player Rank ก๊วนน้ามาด
          </h2>

          <div className="rank-subtitle">
            Ranking & Player Points
          </div>

        </div>


        <div className="rank-header-actions">

          <div className="rank-player-count">
            👥 {filteredPlayers.length} คน
          </div>

          <button
            type="button"
            className="rank-monitor-button"
            onClick={openMonitor}
          >
            🏸 Live Match
          </button>

        </div>

      </div>


      {/* ================= INFO ================= */}

      <div className="rank-info-card">

        <div className="rank-info-header">

          <span className="rank-info-icon">
            ℹ️
          </span>

          <div>

            <div className="rank-info-title">
              ระบบ Ranking
            </div>

            <div className="rank-info-subtitle">
              คะแนนจะเปลี่ยนตามผลการแข่งขัน
            </div>

          </div>

        </div>


        <div className="rank-rule-list">

          {/* WIN */}

          <div className="rank-rule win">

            <span className="rule-icon">
              🏆
            </span>

            <div>
              <strong>
                ชนะ
              </strong>

              <span>
                คะแนนเพิ่ม
              </span>
            </div>

          </div>


          {/* LOSE */}

          <div className="rank-rule lose">

            <span className="rule-icon">
              ❌
            </span>

            <div>
              <strong>
                แพ้
              </strong>

              <span>
                คะแนนลด
              </span>
            </div>

          </div>


          {/* DRAW */}

          <div className="rank-rule draw">

            <span className="rule-icon">
              🤝
            </span>

            <div>
              <strong>
                เสมอ
              </strong>

              <span>
                คะแนนเท่าเดิม
              </span>
            </div>

          </div>

        </div>


        <div className="rank-auto-message">

          ⚡ เมื่อคะแนนถึงเกณฑ์ของ Rank
          ระบบจะปรับ{' '}

          <strong>
            Rank ขึ้นหรือลงอัตโนมัติ
          </strong>

        </div>

      </div>


      {/* ================= FILTER ================= */}

      <div className="rank-filter-card">

        <div className="rank-filter-title">
          Filter Rank
        </div>


        <div className="rank-buttons">

          {RANKS.map(rank => (

            <button
              key={rank}
              type="button"
              className={
                rankFilter === rank
                  ? 'rank-filter-btn active'
                  : 'rank-filter-btn'
              }
              onClick={() =>
                setRankFilter(rank)
              }
            >

              {rank === 'ALL'
                ? 'ทั้งหมด'
                : rank}

            </button>

          ))}

        </div>

<div className="rank-extra-filter">

  <input
    type="text"
    className="rank-search"
    placeholder="🔎 ค้นหาชื่อผู้เล่น..."
    value={search}
    onChange={e =>
      setSearch(e.target.value)
    }
  />

  <div className="rank-profile-hint">
    <span className="rank-profile-hint-icon">
      👆
    </span>

    <span>
      คลิกที่
      <strong> รูปหรือชื่อผู้เล่น </strong>
      เพื่อดูโปรไฟล์ ประวัติการแข่งขัน และอัปเดตรูปโปรไฟล์
    </span>
  </div>

</div>

      </div>


      {/* ================= TABLE ================= */}

      <div className="rank-table-card">

        {loading ? (

          <div className="rank-loading">
            กำลังโหลด...
          </div>

        ) : (

          <div className="rank-table-scroll">

            <table className="rank-table">

              <thead>

                <tr>

                  <th>
                    อันดับ
                  </th>

                  <th>
                    ผู้เล่น
                  </th>

                  <th>
                    Rank
                  </th>

                  <th>
                    Point
                  </th>

                  <th>
                    ล่าสุด
                  </th>

                  <th>
                    อันดับ
                  </th>

                </tr>

              </thead>


              <tbody>

                {filteredPlayers.map(
                  player => (

                    <tr
                      key={
                        player.user_id
                      }
                    >

                      {/* POSITION */}

                      <td className="rank-number">

                        {player.position === 1
                          ? '🥇'
                          : player.position === 2
                          ? '🥈'
                          : player.position === 3
                          ? '🥉'
                          : `#${player.position}`}

                      </td>


                      {/* PLAYER */}

                    <td className="rank-player-name">

  <button
    type="button"
    className="rank-player-link"
    onClick={() =>
      openPlayerHistory(player)
    }
    title={`ดูประวัติของ ${player.name}`}
  >

    <span className="rank-player-avatar">

      {(player.avatarUrl ||
        player.avatar_url ||
        player.avatar) ? (

        <img
          src={getAvatarSource(
            player.avatarUrl ||
            player.avatar_url ||
            player.avatar
          )}
          alt={player.name || 'ผู้เล่น'}
          onError={event => {
            event.currentTarget.style.display =
              'none';

            const fallback =
              event.currentTarget.nextElementSibling;

            if (fallback) {
              fallback.style.display = 'grid';
            }
          }}
        />

      ) : null}

      <span
        className="rank-player-avatar-fallback"
        style={{
          display:
            (
              player.avatarUrl ||
              player.avatar_url ||
              player.avatar
            )
              ? 'none'
              : 'grid'
        }}
      >
        {player.name
          ?.trim()
          ?.charAt(0)
          ?.toUpperCase() || '🏸'}
      </span>

    </span>

    <span className="rank-player-name-text">
      {player.name}
    </span>

  </button>

</td>


                      {/* RANK */}

                      <td>

                        <span className="rank-badge">
                          {player.rank || '-'}
                        </span>

                      </td>


                      {/* POINT */}

                      <td>

                        <span className="rank-point">
                          {player.point ?? 0}
                        </span>

                      </td>


                      {/* POINT CHANGE */}

                      <td>

                        {renderPointChange(
                          player.latest_change
                        )}

                      </td>


                      {/* MOVEMENT */}

                      <td>

                        {renderMovement(
                          player
                        )}

                      </td>

                    </tr>

                  )
                )}


                {filteredPlayers.length === 0 && (

                  <tr>

                    <td
                      colSpan="6"
                      className="rank-empty"
                    >
                      ไม่พบผู้เล่น
                    </td>

                  </tr>

                )}

              </tbody>

            </table>

          </div>

        )}

      </div>

    </div>
  );
}


export default RankTable;